/**
 * Stock Photos Browser Component
 * Search, download, and manage stock photos from Pexels
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    StockImage,
    ArchivedStockImage,
    searchStockPhotos,
    downloadStockPhoto,
    getStockArchive,
    deleteStockPhoto,
    UnifiedFile
} from '../../../../services/mediaApi';

interface StockPhotosBrowserProps {
    theme?: 'light' | 'dark';
    language?: 'en' | 'sv';
    onSelect?: (file: UnifiedFile) => void;
}

type ViewMode = 'search' | 'archive';

const StockPhotosBrowser: React.FC<StockPhotosBrowserProps> = ({
    theme = 'dark',
    language = 'en',
    onSelect
}) => {
    const isDark = theme === 'dark';

    // State
    const [viewMode, setViewMode] = useState<ViewMode>('archive');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<StockImage[]>([]);
    const [archivedImages, setArchivedImages] = useState<ArchivedStockImage[]>([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState<string | null>(null);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Translations
    const t = {
        en: {
            search: 'Search Stock Photos',
            archive: 'My Archive',
            searchPlaceholder: 'Search for images (e.g., beach, hotel, sunset)...',
            noResults: 'No images found. Try a different search term.',
            noArchive: 'No archived images yet. Search and download some!',
            downloading: 'Downloading...',
            downloaded: 'Downloaded to archive!',
            alreadyExists: 'Already in archive',
            downloadError: 'Download failed',
            delete: 'Delete',
            deleted: 'Deleted from archive',
            credit: 'Photo by',
            selectImage: 'Select',
            loadMore: 'Load More'
        },
        sv: {
            search: 'Sök Stockfoton',
            archive: 'Mitt Arkiv',
            searchPlaceholder: 'Sök efter bilder (t.ex. strand, hotell, solnedgång)...',
            noResults: 'Inga bilder hittades. Prova ett annat sökord.',
            noArchive: 'Inga arkiverade bilder ännu. Sök och ladda ner några!',
            downloading: 'Laddar ner...',
            downloaded: 'Nedladdad till arkiv!',
            alreadyExists: 'Finns redan i arkivet',
            downloadError: 'Nedladdning misslyckades',
            delete: 'Radera',
            deleted: 'Raderad från arkiv',
            credit: 'Foto av',
            selectImage: 'Välj',
            loadMore: 'Ladda fler'
        }
    }[language] || {
        search: 'Search Stock Photos',
        archive: 'My Archive',
        searchPlaceholder: 'Search for images (e.g., beach, hotel, sunset)...',
        noResults: 'No images found. Try a different search term.',
        noArchive: 'No archived images yet. Search and download some!',
        downloading: 'Downloading...',
        downloaded: 'Downloaded to archive!',
        alreadyExists: 'Already in archive',
        downloadError: 'Download failed',
        delete: 'Delete',
        deleted: 'Deleted from archive',
        credit: 'Photo by',
        selectImage: 'Select',
        loadMore: 'Load More'
    };

    // Show message
    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3000);
    };

    // Load archive
    const loadArchive = useCallback(async () => {
        setLoading(true);
        const result = await getStockArchive();
        if (result.success && result.images) {
            setArchivedImages(result.images);
        }
        setLoading(false);
    }, []);

    // Search stock photos
    const handleSearch = async (newPage = 1) => {
        if (!searchQuery.trim()) return;

        setLoading(true);
        const result = await searchStockPhotos(searchQuery, 'pexels', { page: newPage, perPage: 12 });

        if (result.success && result.data) {
            if (newPage === 1) {
                setSearchResults(result.data.images);
            } else {
                setSearchResults(prev => [...prev, ...result.data!.images]);
            }
            setTotalPages(result.data.totalPages);
            setPage(newPage);
        } else {
            showMessage('error', result.error || 'Search failed');
        }
        setLoading(false);
    };

    // Download image to archive
    const handleDownload = async (image: StockImage) => {
        setDownloading(image.id);
        const result = await downloadStockPhoto(image);
        setDownloading(null);

        if (result.success) {
            if (result.result) {
                showMessage('success', t.downloaded);
                loadArchive(); // Refresh archive
            }
        } else {
            showMessage('error', result.error || t.downloadError);
        }
    };

    // Delete from archive
    const handleDelete = async (image: ArchivedStockImage) => {
        if (!confirm(language === 'sv' ? 'Är du säker?' : 'Are you sure?')) return;

        const result = await deleteStockPhoto(image.filename, image.source);
        if (result.success) {
            showMessage('success', t.deleted);
            setArchivedImages(prev => prev.filter(i => i.filename !== image.filename));
        } else {
            showMessage('error', result.error || 'Delete failed');
        }
    };

    // Select archived image for use
    const handleSelect = (image: ArchivedStockImage) => {
        if (onSelect) {
            onSelect({
                name: image.filename,
                isDirectory: false,
                size: image.size,
                modifiedAt: image.downloadedAt,
                path: image.url,
                url: image.url,
                source: 'stock-archive'
            });
        }
    };

    // Load archive on mount and when switching to archive view
    useEffect(() => {
        if (viewMode === 'archive') {
            loadArchive();
        }
    }, [viewMode, loadArchive]);

    return (
        <div className={`h-full flex flex-col ${isDark ? 'bg-slate-900 text-white' : 'bg-gray-50 text-slate-900'}`}>
            {/* Header with Tabs */}
            <div className={`flex-shrink-0 px-4 py-3 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <span className="material-icons text-purple-500">photo_library</span>
                        <h2 className="font-bold text-lg">Stock Photos</h2>
                    </div>

                    {/* View Mode Toggle */}
                    <div className={`flex rounded-xl overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                        <button
                            onClick={() => setViewMode('archive')}
                            className={`px-4 py-2 text-sm font-medium transition-all ${viewMode === 'archive' ? 'bg-purple-600 text-white' : ''}`}
                        >
                            <span className="material-icons text-sm mr-1 align-middle">inventory_2</span>
                            {t.archive}
                        </button>
                        <button
                            onClick={() => setViewMode('search')}
                            className={`px-4 py-2 text-sm font-medium transition-all ${viewMode === 'search' ? 'bg-purple-600 text-white' : ''}`}
                        >
                            <span className="material-icons text-sm mr-1 align-middle">search</span>
                            {t.search}
                        </button>
                    </div>
                </div>

                {/* Search Mode Controls */}
                {viewMode === 'search' && (
                    <div className="flex flex-col sm:flex-row gap-3">
                        {/* Search Input */}
                        <div className={`flex-1 flex items-center gap-2 px-4 py-2 rounded-xl ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                            <span className="material-icons text-slate-400 text-sm">search</span>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch(1)}
                                placeholder={t.searchPlaceholder}
                                className={`w-full bg-transparent outline-none text-sm ${isDark ? 'placeholder:text-slate-500' : 'placeholder:text-gray-400'}`}
                            />
                            <button
                                onClick={() => handleSearch(1)}
                                disabled={loading || !searchQuery.trim()}
                                className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? '...' : 'Go'}
                            </button>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span className="material-icons text-green-500 text-sm">check_circle</span>
                            Powered by Pexels
                        </div>
                    </div>
                )}
            </div>

            {/* Message */}
            {message && (
                <div className={`mx-4 mt-3 px-4 py-2 rounded-lg ${message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {message.text}
                </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
                {loading && searchResults.length === 0 && archivedImages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <div className="animate-spin w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{loading ? 'Loading...' : ''}</p>
                        </div>
                    </div>
                ) : viewMode === 'search' ? (
                    // Search Results
                    searchResults.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <span className="material-icons text-6xl text-slate-600 mb-3">image_search</span>
                            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                                {searchQuery ? t.noResults : t.searchPlaceholder}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {searchResults.map(image => (
                                    <div
                                        key={image.id}
                                        className={`group relative aspect-square rounded-xl overflow-hidden border transition-all ${isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white shadow-sm'}`}
                                    >
                                        <img
                                            src={image.thumb}
                                            alt={image.alt}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />

                                        {/* Overlay */}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                                            <button
                                                onClick={() => handleDownload(image)}
                                                disabled={downloading === image.id}
                                                className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                                            >
                                                <span className={`material-icons text-sm ${downloading === image.id ? 'animate-spin' : ''}`}>
                                                    {downloading === image.id ? 'sync' : 'download'}
                                                </span>
                                                {downloading === image.id ? t.downloading : 'Download'}
                                            </button>
                                        </div>

                                        {/* Credit */}
                                        <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                                            <p className="text-xs text-white/80 truncate">
                                                {t.credit} <a href={image.credit.link} target="_blank" rel="noopener noreferrer" className="underline">{image.credit.name}</a>
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Load More */}
                            {page < totalPages && (
                                <div className="text-center mt-6">
                                    <button
                                        onClick={() => handleSearch(page + 1)}
                                        disabled={loading}
                                        className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {loading ? '...' : t.loadMore}
                                    </button>
                                </div>
                            )}
                        </>
                    )
                ) : (
                    // Archive View
                    archivedImages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <span className="material-icons text-6xl text-slate-600 mb-3">inventory_2</span>
                            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t.noArchive}</p>
                            <button
                                onClick={() => setViewMode('search')}
                                className="mt-4 px-4 py-2 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700"
                            >
                                {t.search}
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {archivedImages.map(image => (
                                <div
                                    key={image.filename}
                                    className={`group relative aspect-square rounded-xl overflow-hidden border transition-all cursor-pointer ${isDark ? 'border-slate-700 bg-slate-800 hover:border-purple-500' : 'border-gray-200 bg-white shadow-sm hover:border-purple-400'}`}
                                    onClick={() => handleSelect(image)}
                                >
                                    <img
                                        src={image.url}
                                        alt={image.alt || image.filename}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />

                                    {/* Source Badge */}
                                    <div className="absolute top-2 left-2">
                                        <span className="px-2 py-0.5 text-xs font-medium rounded-full capitalize bg-green-500/80 text-white">
                                            pexels
                                        </span>
                                    </div>

                                    {/* Actions Overlay */}
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                        {onSelect && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleSelect(image); }}
                                                className="px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700"
                                            >
                                                {t.selectImage}
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDelete(image); }}
                                            className="p-1.5 bg-red-500/80 text-white rounded-lg hover:bg-red-600"
                                        >
                                            <span className="material-icons text-sm">delete</span>
                                        </button>
                                    </div>

                                    {/* Filename */}
                                    <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                                        <p className="text-xs text-white truncate" title={image.filename}>
                                            {image.filename}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default StockPhotosBrowser;
